import { ChevronDown, ChevronRight, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import {
  Chip,
  KpiTile,
  PageHeader,
  PaginationFooter,
  ProvenanceBadge,
  StatusDot,
  type StatusDotStatus,
} from "@/components/comms";
import {
  COMMS_PAGE_SIZE,
  type CommsState,
  type ShareMode,
  type ShareStatus,
  type ShareSummaryRow,
  tierHeldLabel,
  type TierRef,
  useShareQuarters,
  useShareSummary,
} from "@/lib/comms";
import { logger } from "@/lib/logger";
import { cn, formatSignedPercent } from "@/lib/utils";

const DOT: Record<ShareStatus, StatusDotStatus> = { ok: "ok", warn: "progress", risk: "risk" };

const QTR_OPTIONS = ["Q1", "Q2", "Q3", "Q4"];

const LIVE_COLS = ["Account", "Share before comms", "Tier held", "Gap", "Comms"];
const CLOSED_COLS = ["Account", "Share before comms", "Final quarter share", "Difference", "Comms"];

const CTX_TEXT: Record<ShareMode, string> = {
  live: "Accounts are triaged against the programme tiers (T1 30% · T2 50% · T3 75%). The agent auto-nudges everyone below 75% with how close they are to their relevant tiers; 75–79% waits on your approval; 80% and above is left alone.",
  future: "This quarter hasn't started yet — no share data recorded.",
  closed:
    "Closed quarter — where each account finished versus where it started, and whether a nudge went out.",
};

function CommsCell({ comms, onApprove }: { comms: CommsState; onApprove: () => void }) {
  if (comms === "approve") {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onApprove();
        }}
        className="bg-jj-red hover:bg-jj-red-dk cursor-pointer rounded-full px-3.5 py-1.5 text-xs font-semibold whitespace-nowrap text-white"
      >
        Approve &amp; send
      </button>
    );
  }
  // Neutral grey for both sent states — the mockup keeps auto-sends
  // matter-of-fact rather than a success signal (.comms-sent / .comms-auto).
  if (comms === "autosent") {
    return (
      <span className="text-wg-07 inline-flex items-center gap-2">
        Sent
        <Chip>Auto</Chip>
      </span>
    );
  }
  if (comms === "sent") {
    return <span className="text-wg-07">Sent</span>;
  }
  if (comms === "skipped") {
    return <span className="text-wg-07">Skipped</span>;
  }
  return <span className="text-wg-06">Not sent</span>;
}

/**
 * Distance to the tier the row is measured against: points still needed to
 * reach the next tier up, or — once the top tier is held — points over it.
 */
function GapCell({ gap, target }: { gap: number; target?: TierRef }) {
  const tier = target?.tier ?? "tier";
  if (gap > 0) {
    return (
      <span className="text-risk font-semibold">
        {gap}% to {tier}
      </span>
    );
  }
  // Exactly on the threshold: "+0% over" would read as a rounding artefact.
  if (gap === 0) {
    return <span className="text-ok font-semibold">exactly on {tier}</span>;
  }
  return (
    <span className="text-ok font-semibold">
      +{-gap}% over {tier}
    </span>
  );
}

/** Final-vs-before movement (closed rows). */
function DiffCell({ diff }: { diff: number }) {
  return diff >= 0 ? (
    <span className="text-ok font-semibold">+{diff}%</span>
  ) : (
    <span className="text-risk font-semibold">{diff}%</span>
  );
}

export function ShareListPage() {
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [offset, setOffset] = useState(0);
  const [sel, setSel] = useState<{ year: string; qtr: string } | null>(null);
  const { data: quartersData } = useShareQuarters();

  // Debounce the search box so we don't query on every keystroke; a new term
  // always jumps back to the first page.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedTerm(term.trim());
      setOffset(0);
    }, 300);
    return () => clearTimeout(t);
  }, [term]);

  // Default to the current quarter once quarters load; user picks override it.
  const current = quartersData?.current;
  const year = sel?.year ?? current?.slice(0, 4) ?? "";
  const qtr = sel?.qtr ?? (current ? `Q${current.slice(5)}` : "");
  const quarterKey = year && qtr ? `${year}${qtr}` : null;
  const {
    data: summary,
    isError: summaryError,
    isFetching: summaryFetching,
  } = useShareSummary(quarterKey, {
    limit: COMMS_PAGE_SIZE,
    offset,
    q: debouncedTerm || undefined,
  });

  const years = [...new Set((quartersData?.quarters ?? []).map((q) => q.slice(0, 4)))]
    .sort()
    .reverse();
  const mode = summary?.mode;
  const rows = summary?.rows ?? [];
  const cols = mode === "closed" ? CLOSED_COLS : LIVE_COLS;

  const changeQuarter = (next: { year: string; qtr: string }) => {
    setSel(next);
    setOffset(0);
  };

  const openDetail = (row: ShareSummaryRow, withDraft: boolean) => {
    logger.debug("[share]", "open account detail", row.id, { withDraft });
    void navigate(`/share/${row.id}${withDraft ? "?draft=1" : ""}`);
  };

  return (
    <div>
      <PageHeader
        backTo="/home"
        backLabel="Home"
        title="Order Share Monitoring Proactive Comms"
        subtitle="Order share monitoring · preferred customers"
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <span className="text-wg-08 text-sm font-semibold">Preferred customer list</span>
        <label className="border-wg-03 focus-within:border-wg-05 flex w-64 items-center gap-2 rounded-full border bg-white px-3.5 py-2">
          <Search className="text-wg-06 size-4 shrink-0" />
          <input
            type="text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search customer"
            autoComplete="off"
            className="w-full bg-transparent text-sm outline-none"
          />
        </label>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="grid flex-1 gap-3 sm:grid-cols-3">
          <KpiTile
            label="At / above T3 (75%)"
            value={summary ? summary.kpis.at_or_above_top_tier.toLocaleString("en-US") : "—"}
          />
          <KpiTile
            label="Total nudges sent"
            value={summary ? summary.kpis.total_nudges_sent.toLocaleString("en-US") : "—"}
          />
          <KpiTile
            label="Avg. share difference after nudge"
            value={summary ? formatSignedPercent(summary.kpis.avg_share_diff_after_nudge) : "—"}
            valueClassName={
              (summary?.kpis.avg_share_diff_after_nudge ?? 0) < 0 ? "text-risk" : "text-ok"
            }
          />
        </div>
        <div className="flex gap-2">
          <span className="relative">
            <select
              value={year}
              onChange={(e) => changeQuarter({ year: e.target.value, qtr })}
              aria-label="Year"
              className="border-wg-03 cursor-pointer appearance-none rounded-lg border bg-white py-2 pr-9 pl-3 text-sm font-medium"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <ChevronDown className="text-wg-07 pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2" />
          </span>
          <span className="relative">
            <select
              value={qtr}
              onChange={(e) => changeQuarter({ year, qtr: e.target.value })}
              aria-label="Quarter"
              className="border-wg-03 cursor-pointer appearance-none rounded-lg border bg-white py-2 pr-9 pl-3 text-sm font-medium"
            >
              {QTR_OPTIONS.map((q) => (
                <option key={q} value={q}>
                  {`${year}${q}` === current ? `${q} (current)` : q}
                </option>
              ))}
            </select>
            <ChevronDown className="text-wg-07 pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2" />
          </span>
        </div>
      </div>

      {mode && (
        <div className="mt-5 flex flex-wrap items-center gap-3 text-sm">
          <span className="border-wg-03 text-wg-08 inline-flex items-center gap-2 rounded-full border bg-white px-3 py-1 text-xs font-semibold">
            {mode === "live" && <StatusDot status="risk" pulse className="size-2" />}
            {mode === "live"
              ? "Live quarter"
              : mode === "future"
                ? "Upcoming quarter"
                : "Closed quarter"}
          </span>
          <span className="text-wg-07">{CTX_TEXT[mode]}</span>
        </div>
      )}

      {mode === "live" && (
        <div className="text-wg-07 mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs">
          <span className="flex items-center gap-1.5">
            <StatusDot status="ok" className="size-2.5" /> 80%+ — no comms
          </span>
          <span className="flex items-center gap-1.5">
            <StatusDot status="progress" className="size-2.5" /> 75–79% — drafted for your approval
          </span>
          <span className="flex items-center gap-1.5">
            <StatusDot status="risk" className="size-2.5" /> Below 75% — nudged automatically
          </span>
        </div>
      )}

      <div className="border-wg-03 mt-4 overflow-x-auto rounded-xl border bg-white">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-wg-03 border-b">
              {cols.map((c, i) => (
                <th
                  key={c}
                  className={cn(
                    "text-wg-07 px-4 py-3 text-left text-[11px] font-semibold tracking-[0.08em] uppercase",
                    i >= 2 && i <= 3 && "hidden md:table-cell",
                  )}
                >
                  {c}
                </th>
              ))}
              <th className="w-10" />
            </tr>
          </thead>
          <tbody className={cn(summaryFetching && summary && "opacity-60")}>
            {summaryError ? (
              <tr>
                <td colSpan={6} className="text-wg-06 px-4 py-10 text-center">
                  Couldn’t load accounts — please try again.
                </td>
              </tr>
            ) : !summary ? (
              <tr>
                <td colSpan={6} className="text-wg-06 px-4 py-10 text-center">
                  Loading accounts…
                </td>
              </tr>
            ) : summary.total === 0 ? (
              <tr>
                <td colSpan={6} className="text-wg-06 px-4 py-10 text-center">
                  {mode === "future"
                    ? "No data yet for this quarter."
                    : debouncedTerm
                      ? `No accounts match "${debouncedTerm}".`
                      : "No accounts match your search."}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  tabIndex={0}
                  onClick={() => openDetail(row, false)}
                  onKeyDown={(e) => {
                    // Only keys on the row itself — inner controls (Approve
                    // & send) handle their own activation.
                    if (e.target !== e.currentTarget) {
                      return;
                    }
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openDetail(row, false);
                    }
                  }}
                  className="border-wg-03 hover:bg-wg-01 focus-visible:outline-jj-red cursor-pointer border-b last:border-b-0 focus-visible:outline-2 focus-visible:-outline-offset-2"
                >
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-2.5 font-medium">
                      <StatusDot status={DOT[row.status]} />
                      {row.name}
                      <ProvenanceBadge provenance={row.provenance} />
                    </span>
                  </td>
                  <td className="px-4 py-3 tabular-nums">{row.share_before}%</td>
                  {mode === "closed" ? (
                    <>
                      <td className="hidden px-4 py-3 tabular-nums md:table-cell">
                        {row.share_final}%
                      </td>
                      <td className="hidden px-4 py-3 tabular-nums md:table-cell">
                        <DiffCell diff={row.difference ?? 0} />
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="hidden px-4 py-3 tabular-nums md:table-cell">
                        {tierHeldLabel(row.tier_held ?? null)}
                      </td>
                      <td className="hidden px-4 py-3 tabular-nums md:table-cell">
                        <GapCell gap={row.gap ?? 0} target={row.target_tier} />
                      </td>
                    </>
                  )}
                  <td className="px-4 py-3">
                    <CommsCell comms={row.comms} onApprove={() => openDetail(row, true)} />
                  </td>
                  <td className="text-wg-05 px-4 py-3">
                    <ChevronRight className="size-4" />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {summary && summary.total > 0 && (
          <PaginationFooter
            offset={summary.offset}
            limit={summary.limit}
            total={summary.total}
            disabled={summaryFetching}
            onPrev={() => setOffset((o) => Math.max(0, o - COMMS_PAGE_SIZE))}
            onNext={() => setOffset((o) => o + COMMS_PAGE_SIZE)}
          />
        )}
      </div>
    </div>
  );
}
